import { getProperty } from "dot-prop";
import { z, ZodIssueCode } from "zod";
import {
  classificationCategory,
  classificationType,
  E3_561_007,
  measurementUnit,
  otherIncome,
  taxType,
  vatCategory,
  vatExemption, WithoutVAT,
  ZeroVAT
} from "~/mydata";
import { ValuesOf } from "~/mydata/typeutils";
import {
  invoiceMeta,
  InvoiceType, posReceipt,
  retailSalesReceipt,
  retailServicesReceipt,
  salesInvoice,
  salesInvoiceEU,
  salesInvoiceNonEU,
  salesInvoiceSelfPricing,
  servicesInvoice,
  servicesInvoiceEU,
  servicesInvoiceNonEU
} from "~/onesign/invoice";
import { isValidClassification } from "~/onesign/invoice-classifications";
import { findTax } from "~/onesign/taxes";
import { amount } from "./amount";

export const LineItem = "line_item";
export const OtherTaxWithVAT = "other_tax_with_vat";
export const FeeWithVAT = "fee_with_vat";
export const GiftCard = "gift_card";
export const invoiceItemTypes = [
  LineItem,
  OtherTaxWithVAT,
  FeeWithVAT,
  GiftCard,
] as const;

export const invoiceItemTypeNames: Record<InvoiceItemType, string> = {
  [LineItem]: "Γραμμή Φ.Π.Α",
  [OtherTaxWithVAT]: "Γραμμή λοιπών φόρων με Φ.Π.Α",
  [FeeWithVAT]: "Γραμμή τέλους με Φ.Π.Α",
  [GiftCard]: "Δωροεπιταγή",
};

export type InvoiceItemType = ValuesOf<typeof invoiceItemTypes>;

export const Good = "good";
export const Service = "service";
export const productTypes = [Good, Service];

export const productTypeNames: Record<ProductType, string> = {
  [Good]: "Αγαθό",
  [Service]: "Υπηρεσία",
};

export type ProductType = ValuesOf<typeof productTypes>;

export const isInvoiceItemType = (input: unknown): input is InvoiceItemType =>
  typeof input === "string" && invoiceItemTypes.includes(input as any);

const issueZeroVATWithoutExcemption = {
  message: "Η κατηγορία εξαίρεσης είναι υποχρεωτική για μηδενικό Φ.Π.Α",
  path: ["vatExemptionCategory"],
};
const issueVATWithRate = {
  message: "Η γραμμή παραστατικού υπάγεται σε Φ.Π.Α.",
  path: ["vatCategory"],
};
const issueWithZeroVAT = {
  message: "Η γραμμή παραστατικού έχει υποχρεωτικά μηδενικό Φ.Π.Α.",
  path: ["vatCategory"],
};
const issueNonZeroVATWithExcemption = {
  message:
    "Η κατηγορία εξαίρεσης δεν ορίζεται για μη μηδενικό Φ.Π.Α και για τιμολόγια άνευ Φ.Π.Α",
  path: ["vatExemptionCategory"],
};

export const itemVAT = z
  .object({
    vatCategory: z.nativeEnum(vatCategory),
    vatExemptionCategory: z.nativeEnum(vatExemption).optional(),
  })
  .refine(
    (vat) =>
      vat.vatCategory !== ZeroVAT
        ? vat.vatExemptionCategory === undefined
        : true,
    issueNonZeroVATWithExcemption
  )
  .refine(
    (vat) =>
      vat.vatCategory === ZeroVAT
        ? vat.vatExemptionCategory !== undefined
        : true,
    issueZeroVATWithoutExcemption
  );

export const isPositiveAmount = (amount: string) => Number(amount) > 0;
export const isZeroOrPositiveAmount = (amount: string) => Number(amount) >= 0;
export const isZeroAmount = (amount: string) => Number(amount) === 0;
const issueNonZeroAmountOnRateTax = {
  message:
    "Δεν επιτρέπεται η δήλωση ποσού, ο φόρος υπολογίζεται επί της αξίας με Φ.Π.Α.",
  path: ["taxAmount"],
};
const issueNonZeroAmountOnPerItemTax = {
  message:
    "Δεν επιτρέπεται η δήλωση ποσού, ο φόρος υπολογίζεται βάση ποσότητας.",
  path: ["taxAmount"],
};

export const rawItemTax = z.object({
  type: z.nativeEnum(taxType, {
    invalid_type_error: "Μή έγκυρος τύπος φόρου",
  }),
  category: z.number().optional(),
  customAmount: amount.optional().default("0"),
  inputAmount: amount.optional().default("0")
});
export const itemTax = rawItemTax.superRefine((tax, ctx) => {
  const meta = findTax(tax.type, tax.category);
  if (meta === undefined) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: `Μη έγκυρη κατηγορία φόρου`,
      path: ["category"],
    });
  } else if (
    getProperty(meta, "customAmount", false) &&
    !isPositiveAmount(tax.customAmount)
  ) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: `Το ποσό φόρου είναι υποχρεωτικά μεγαλύτερο του 0 για αυτο το φόρο.`,
      path: ["customAmount"],
    });
  } else if (
    getProperty(meta, "rate") !== undefined &&
    !isZeroAmount(tax.customAmount)
  ) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: issueNonZeroAmountOnRateTax.message,
      path: ["customAmount"],
    });
  } else if (
    getProperty(meta, "item") !== undefined &&
    !isZeroAmount(tax.customAmount)
  ) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: issueNonZeroAmountOnPerItemTax.message,
      path: ["customAmount"],
    });
  }
});

export type ItemTax = z.output<typeof itemTax>;

export const invoiceItemCommonShape = {
  code: z.string().optional(),
  description: z.string().min(1),
  netValuePerUnit: amount.refine(isPositiveAmount, {
    message: "Το ποσό πρέπει να είναι μεγαλύτερο του μηδέν. ",
  }),
  quantity: amount.optional().default("1"),
  measurementUnit: z.nativeEnum(measurementUnit).optional(),
  discount: amount.optional().default("0"),
  productType: z.enum([Good, Service]).optional(),
};
const itemWithVAT = itemVAT.refine(
  (vat) => vat.vatCategory !== vatCategory.WithoutVAT,
  issueVATWithRate
);

const itemWithOutVAT = itemVAT.refine(
  (vat) => vat.vatCategory === vatCategory.WithoutVAT,
  issueVATWithRate
);

export const itemWithVATCommonShape = {
  itemType: z.literal(LineItem),
  taxes: z.array(itemTax).optional(),
  vat: itemWithVAT,
  ...invoiceItemCommonShape,
};

export const itemWithOutVATCommonShape = {
  itemType: z.literal(LineItem),
  taxes: z.array(itemTax).optional(),
  vat: itemWithOutVAT,
  ...invoiceItemCommonShape,
};

export const itemForeignVATCommonShape = {
  itemType: z.literal(LineItem),
  taxes: z.array(itemTax).optional(),
  vat: z.object({
    vatCategory: z.literal(vatCategory.ZeroVAT),
    vatExemptionCategory: z.nativeEnum(vatExemption),
  }),
  ...invoiceItemCommonShape,
};

const itemVatClassification = z.object({
  type: z.enum(classificationType)
});

const invoiceItemVatClassification = (invoiceType: InvoiceType) =>
  itemVatClassification.superRefine(({type}, ctx) => {
    if(!type.startsWith("VAT_")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Μη επιτρεπτός χαρακτηρισμός φόρου`
      })
    }
  });

const taxWithVATCommonShape = {
  ...invoiceItemCommonShape,
  vat: itemVAT,
  classification: z
    .object({
      category: z.literal(otherIncome),
      type: z.literal(E3_561_007),
    })
    .optional()
    .default({
      category: otherIncome,
      type: E3_561_007,
    }),
  vatClassification: itemVatClassification
};

export const itemOtherTaxWithVAT = z.object({
  itemType: z.literal(OtherTaxWithVAT),
  ...taxWithVATCommonShape,
  taxes: z
    .array(
      itemTax.refine((tax) => tax.category === taxType.OtherTax, {
        message: `Μη έγκυρη κατηγορία φόρου`,
        path: ["category"],
      })
    )
    .optional(),
});

export const itemFeeWithVAT = z.object({
  itemType: z.literal(FeeWithVAT),
  ...taxWithVATCommonShape,
  taxes: z
    .array(
      itemTax.refine((tax) => tax.category === taxType.Fee, {
        message: `Μη έγκυρη κατηγορία φόρου`,
        path: ["category"],
      })
    )
    .optional(),
});

const itemClassification = z.object({
  category: z.enum(classificationCategory),
  type: z.enum(classificationType),
});
export const itemGiftCard = z.object({
  itemType: z.literal(GiftCard),
  taxes: z.array(itemTax).optional(),
  vat: itemVAT.refine(
    (vat) => vat.vatCategory !== vatCategory.WithoutVAT,
    issueVATWithRate
  ),
  ...invoiceItemCommonShape,
  classification: itemClassification,
  vatClassification: itemVatClassification
});
const invoiceItemClassification = (invoiceType: InvoiceType) =>
  itemClassification.superRefine(({ category, type }, ctx) => {
    if (!isValidClassification(invoiceType, category, type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Μη επιτρεπτός συνδυασμός χαρακτηρισμού εσόδων/εξόδων για ${invoiceMeta[invoiceType].name}`,
      });
    }
  });

export const lineItem = {
  [salesInvoice]: z.object({
    ...itemWithVATCommonShape,
    classification: invoiceItemClassification(salesInvoice),
    vatClassification: z.undefined()
  }),
  [salesInvoiceSelfPricing]: z.object({
    ...itemWithVATCommonShape,
    classification: invoiceItemClassification(salesInvoiceSelfPricing),
    vatClassification: invoiceItemVatClassification(salesInvoiceSelfPricing)
  }),
  [salesInvoiceEU]: z.object({
    ...itemForeignVATCommonShape,
    classification: invoiceItemClassification(salesInvoiceEU),
    vatClassification: z.undefined()
  }),
  [salesInvoiceNonEU]: z.object({
    ...itemForeignVATCommonShape,
    classification: invoiceItemClassification(salesInvoiceNonEU),
    vatClassification: z.undefined()
  }),
  [servicesInvoice]: z.object({
    ...itemWithVATCommonShape,
    classification: invoiceItemClassification(servicesInvoice),
    vatClassification: z.undefined()
  }),
  [servicesInvoiceEU]: z.object({
    ...itemForeignVATCommonShape,
    classification: invoiceItemClassification(servicesInvoiceEU),
    vatClassification: z.undefined()
  }),
  [servicesInvoiceNonEU]: z.object({
    ...itemForeignVATCommonShape,
    classification: invoiceItemClassification(servicesInvoiceNonEU),
    vatClassification: z.undefined()
  }),
  [retailSalesReceipt]: z.object({
    ...itemWithVATCommonShape,
    classification: invoiceItemClassification(retailSalesReceipt),
    vatClassification: z.undefined()
  }),

  [retailServicesReceipt]: z.object({
    ...itemWithVATCommonShape,
    classification: invoiceItemClassification(retailServicesReceipt),
    vatClassification: z.undefined()
  }),

  [posReceipt]: z.object({
    ...itemWithOutVATCommonShape,
    // classification: invoiceItemClassification(posReceipt),
    // vatClassification: z.undefined()
  }),
};
